import * as React from "react";
import { useEffect, useState } from "react";
import { Box, Button, Checkbox, Divider, FormControlLabel, Menu, Typography } from "@mui/material";
import { translate } from "../../../../translate/translate";


interface FilterMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onApplyFilters: (columns: string[], balance: string[]) => void;
  selectedColumns: string[];
  selectedBalance: string[];
}

const sectionLabelStyle = {
  "& .MuiFormControlLabel-label": {
    fontFamily: "Inter",
    fontSize: "14.5px",
    fontWeight: 500,
    color: "#45485F",
  },
};

const sectionTitleStyle = {
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14.5px",
  lineHeight: "100%",
  margin: "0px 0px 10px 0px",
  color: "#546E7A",
  marginTop: "10px",
};

const FilterMenuComponent = ({
                               anchorEl,
                               open,
                               onClose,
                               onApplyFilters,
                               selectedColumns,
                               selectedBalance,
                             }: FilterMenuProps) => {


  const [tempSelectedColumns, setTempSelectedColumns] = useState<string[]>(selectedColumns);
  const [tempSelectedBalance, setTempSelectedBalance] = useState<string[]>(selectedBalance);

  // Ordem fixa das colunas para manter posição consistente ao (des)marcar filtros
  const columnsOptions = [
    "description",
    "supplier",
    "manufacturer_part_number",
    "final_products",
  ];

  const balanceOptions = ["forecast", "in_transit", "stock", "projected_inventory"];


  useEffect(() => {
    if (open) {
      // sempre garantir que as colunas temporárias sigam a ordem definida em columnsOptions
      setTempSelectedColumns(
        columnsOptions.filter((col) => selectedColumns.includes(col)),
      );
      setTempSelectedBalance(selectedBalance);
    }
  }, [open, selectedColumns, selectedBalance]);

  const handleSelectAllBalance = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setTempSelectedBalance(balanceOptions);
    } else {
      setTempSelectedBalance([]);
    }
  };

  const isAllBalanceSelected = balanceOptions.every((opt) =>
    tempSelectedBalance.includes(opt),
  );

  const handleCheckboxChange =
    (group: "columns" | "balance", option: string) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;

        if (group === "columns") {
          setTempSelectedColumns((prev) => {
            if (isChecked) {
              // adicionar então normalizar pela ordem definida em columnsOptions
              const next = Array.from(new Set([...prev, option]));
              return columnsOptions.filter((col) => next.includes(col));
            }
            // remover e manter a ordem
            return columnsOptions.filter((col) => prev.filter((p) => p !== option).includes(col));
          });
        } else {
          setTempSelectedBalance((prev) =>
            isChecked ? [...prev, option] : prev.filter((item) => item !== option),
          );
        }
      };

  const handleApply = () => {
    // garantir que a lista enviada ao pai respeite a ordem padrão
    const orderedColumns = columnsOptions.filter((col) => tempSelectedColumns.includes(col));
    onApplyFilters(orderedColumns, tempSelectedBalance);
    onClose();
  };


  return (
    <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            width: 250,
          },
        },
      }}
    >
      <Typography sx={sectionTitleStyle}>{translate.t("columns")}:</Typography>
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={tempSelectedColumns.includes("description")}
              onChange={handleCheckboxChange("columns", "description")}
            />
          }
          sx={sectionLabelStyle}
          label={translate.t("description")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={tempSelectedColumns.includes("supplier")}
              onChange={handleCheckboxChange("columns", "supplier")}
            />
          }
          sx={sectionLabelStyle}
          label={translate.t("supplier")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={tempSelectedColumns.includes("manufacturer_part_number")}
              onChange={handleCheckboxChange("columns", "manufacturer_part_number")}
            />
          }
          sx={sectionLabelStyle}
          label={translate.t("manufacturer")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={tempSelectedColumns.includes("final_products")}
              onChange={handleCheckboxChange("columns", "final_products")}
            />
          }
          sx={sectionLabelStyle}
          label={translate.t("models")}
        />
      </Box>
      <Typography sx={sectionTitleStyle}>{translate.t("balance")}:</Typography>
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isAllBalanceSelected}
              indeterminate={
                tempSelectedBalance.length > 0 && !isAllBalanceSelected
              }
              onChange={handleSelectAllBalance}
            />
          }
          label={translate.t("select_all")}
          sx={sectionLabelStyle}
        />
        {balanceOptions.map((option) => (
          <FormControlLabel
            key={option}
            control={
              <Checkbox
                checked={tempSelectedBalance.includes(option)}
                onChange={handleCheckboxChange("balance", option)}
              />
            }
            label={translate.t(option)}
            sx={sectionLabelStyle}
          />
        ))}
      </Box>
      <Divider />
      <Button
        variant="outlined"
        color="primary"
        sx={{ marginTop: "10px" }}
        onClick={handleApply}
        disabled={tempSelectedBalance.length === 0}
      >
        {translate.t("apply")}
      </Button>
    </Menu>
  );
};

export default FilterMenuComponent;
